/* eslint-disable @next/next/no-img-element */
// HERO "Horizonte" (modelo_busca = 14): hero CINEMATOGRÁFICO em tela cheia do modelo
// "Horizonte" — a mídia do tenant (vídeo > banner > capa do vídeo > degradê) ocupa a largura
// toda, sem moldura, com:
//  - zoom lento contínuo (Ken Burns, framer-motion) e PARALLAX no scroll (useScroll +
//    useTransform: a mídia desce mais devagar que a página, o título sobe e esmaece);
//  - sobretítulo (empresa · cidade) com uma linha de "horizonte" que se desenha, e o título
//    (site.frase_busca, a mesma dos heros 7/11/13) revelado PALAVRA A PALAVRA por máscara
//    subindo — em CSS puro, que roda no 1º paint sem esperar o JS (o título nunca nasce com
//    opacity 0; sem JS ele termina visível do mesmo jeito);
//  - painel de busca em VIDRO escuro: abas de transação com indicador que DESLIZA (layoutId),
//    Cidade · Tipo (CampoSelecaoMulti: várias, Daniel 21/10) · Quartos (CampoQuantidadeMulti: vários números) e botão na cor2; "Busca avançada" e CardsBusca;
//  - indicação animada de "role para baixo" (botão que rola até o fim do hero).
// O bloco não depende do topo 11: com qualquer topo ele é só a primeira seção da home.
// A busca (estado/URL/tracking Meta) vem inteira do hook compartilhado useHeroSearch.
// prefers-reduced-motion: sem Ken Burns, parallax, máscara e indicador animado.
// Default export = ErrorBoundary com fallback (padrão WelcomeAurora/Autoral).
import { motion, MotionConfig, useScroll, useTransform } from 'framer-motion';
import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import type { CSSProperties } from 'react';
import { ensureReadableTextColor, isColorLight } from '../../../utils/FunctionsGlobals';
import { getHeroVideoPoster, getTenantVideoCover, useHeroVideoReady } from '../../../utils/heroVideo';
import { genMetaEventId, trackMetaEvent } from '../../../utils/metaPixel';
import HeroVideoCover from '../../utils/HeroVideoCover';
import type { HeroSectionProps } from '../../registry/types';
import { useHeroSearch } from '../../shared/hero-search/useHeroSearch';
import CampoSelecaoMulti from '../../shared/hero-search/CampoSelecaoMulti';
import CampoQuantidadeMulti from '../../shared/hero-search/CampoQuantidadeMulti';
import CardsBusca from '../../shared/cards-busca/CardsBusca';
import DrawerComponent from '../../drawer/DrawerComponent';
// Painel de filtros avançados do Aurora, REAPROVEITADO com a pele do Horizonte (prop `variante`): mesmos
// filtros, mesma URL de /imoveis, modal no desktop / tela cheia no celular, leva junto o que está na barra.
import BuscaAvancadaAurora from '../welcome-aurora/BuscaAvancadaAurora';
import { EASE_HORIZONTE, useMovimentoReduzido } from '../horizonte/horizonte';
import styles from './WelcomeHorizonte.module.css';
import { usePreviaBuilder } from '../../../lib/previaBuilder';

const stripHtml = (value: unknown): string =>
    String(value ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const IconeSeta = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="13 6 19 12 13 18" />
    </svg>
);

const WelcomeHorizonteInner: React.FC<HeroSectionProps> = ({
    allCities,
    allTypes,
    allTransacao,
    allCompany,
    site,
    urlBanner,
    origin,
    codsite2,
    setActiveDrawer,
    activeDrawer,
    favorito,
    isMobile1,
    props,
    allCards,
}) => {
    const { videoParado } = usePreviaBuilder(); // prévia dos cards do CRM: vídeo parado na capa
    const {
        transacoes,
        transacao,
        setTransacao,
        cidade,
        setCidade,
        tipo,
        setTipo,
        dormitorios,
        setDormitorios,
        loading,
        finalize,
        onSubmit,
    } = useHeroSearch({ site, allCities, allTypes, allTransacao, codsite2, origin });
    const reduzido = useMovimentoReduzido();

    // ---- busca avançada (Daniel 14/10: "não abre popup"): o botão da barra abre o painel de filtros, como no
    // Aurora — a URL final é a do contrato de /imoveis (montarUrlAvancada + finalize do hook) e o evento Search
    // leva o MESMO eventId que a listagem usa na dedup do CAPI (como o hook). ----
    const router = useRouter();
    const [avancadaAberta, setAvancadaAberta] = useState(false);
    const [qtdAvancada, setQtdAvancada] = useState(0);
    const fecharAvancada = useCallback(() => setAvancadaAberta(false), []);
    const buscarAvancada = (url: string) => {
        const eventId = genMetaEventId();
        try {
            sessionStorage.setItem('meta:searchEventId', JSON.stringify({ id: eventId, at: Date.now() }));
        } catch {
            /* sessionStorage indisponível */
        }
        trackMetaEvent(site, 'Search', { search_string: 'busca avançada', transaction_type: transacao }, { eventId });
        setAvancadaAberta(false);
        router.push(url).catch(() => undefined);
    };
    const uid = useId().replace(/:/g, '');

    // ---- parallax: progresso do hero saindo da tela (0 = no topo, 1 = já passou) ----
    const secaoRef = useRef<HTMLElement | null>(null);
    const { scrollYProgress } = useScroll({ target: secaoRef, offset: ['start start', 'end start'] });
    const mediaY = useTransform(scrollYProgress, [0, 1], ['0%', '24%']);
    const cabecaY = useTransform(scrollYProgress, [0, 1], ['0%', '-30%']);
    const cabecaOpacidade = useTransform(scrollYProgress, [0, 0.55], [1, 0]);

    // ---- cores: papéis da paleta; onde o fundo é cor do tenant, o texto é CALCULADO ----
    const acao = site?.cor2 || '#c8a46a';
    const corAcaoTexto = ensureReadableTextColor(isColorLight(acao) ? site?.cor11 : site?.cor13, acao) || '#ffffff';
    // aba ativa: indicador claro sobre o vidro escuro; texto calculado sobre o branco.
    const corAbaAtivaTexto = ensureReadableTextColor(site?.cor11, '#ffffff') || '#141821';
    // linha do horizonte no sobretítulo: cor2 se ler sobre o véu escuro; senão branco.
    const corLinha = ensureReadableTextColor(acao, '#11141c', 2.2, '#ffffff', '#ffffff') || '#ffffff';

    // ---- mídia de fundo: vídeo do tenant (fora do celular) > banner "topo" > capa > degradê ----
    const bannerSrc = urlBanner && urlBanner !== 'undefined' ? String(urlBanner).replaceAll('"', '') : '';
    const videoSrc = site?.url_video && !isMobile1 ? String(site.url_video).replaceAll('"', '') : '';
    const heroPoster = getHeroVideoPoster(site?.url_video);
    const heroVideoReady = useHeroVideoReady();
    // No celular o vídeo fica desligado, mas a CAPA dele entra no lugar do degradê (#1271).
    const capaVideo = site?.url_video_poster || heroPoster || getTenantVideoCover(site?.url_video);

    const titulo = useMemo(() => (site?.frase_busca ? stripHtml(site.frase_busca) : 'Encontre o imóvel perfeito para você'), [site?.frase_busca]);
    const subtitulo = useMemo(() => (site?.frase_busca_2 ? stripHtml(site.frase_busca_2) : ''), [site?.frase_busca_2]);
    const palavras = useMemo(() => titulo.split(' ').filter(Boolean), [titulo]);
    const sobretitulo = [site?.Dados?.empresa, site?.Dados?.cidade].filter(Boolean).join(' · ');

    const rolarParaBaixo = () => {
        const el = secaoRef.current;
        if (!el) return;
        const destino = el.getBoundingClientRect().bottom + window.scrollY - 64;
        window.scrollTo({ top: destino, behavior: reduzido ? 'auto' : 'smooth' });
    };

    const varsHero = {
        '--hz-acao': acao,
        '--hz-acao-texto': corAcaoTexto,
        '--hz-linha': corLinha,
    } as CSSProperties;

    return (
        <MotionConfig reducedMotion="user">
            <section ref={secaoRef} className={styles.horizonte} data-builder-section="busca" style={varsHero}>
                <DrawerComponent site={site} activeDrawer={activeDrawer} setActiveDrawer={setActiveDrawer} favorito={favorito || []} menus={props?.menus} />

                {/* ---------- mídia: parallax (externo) + Ken Burns (interno) ---------- */}
                <div className={styles.palco} aria-hidden="true">
                    <motion.div className={styles.parallax} style={reduzido ? undefined : { y: mediaY }}>
                        <motion.div
                            className={styles.kenburns}
                            initial={{ scale: 1.04 }}
                            animate={reduzido ? { scale: 1.04 } : { scale: 1.16 }}
                            transition={reduzido ? { duration: 0 } : { duration: 28, ease: 'linear', repeat: Infinity, repeatType: 'mirror' }}
                        >
                            {videoSrc ? (
                                <video
                                    className={styles.mediaEl}
                                    autoPlay={!videoParado}
                                    muted
                                    loop
                                    playsInline
                                    preload="none"
                                    poster={heroPoster}
                                    src={heroPoster ? (heroVideoReady ? videoSrc : undefined) : videoSrc}
                                >
                                    <track kind="captions" label="Legenda" src="legenda.vtt" />
                                </video>
                            ) : bannerSrc ? (
                                <img className={styles.mediaEl} src={bannerSrc} alt="" decoding="async" />
                            ) : (
                                <HeroVideoCover src={capaVideo} className={styles.mediaEl} fallbackClassName={styles.mediaFallback} />
                            )}
                        </motion.div>
                    </motion.div>
                    <div className={styles.veu} />
                    <div className={styles.vinheta} />
                </div>

                {/* ---------- conteúdo ---------- */}
                <div className={`${styles.conteudo} ${reduzido ? styles.semMovimento : ''}`}>
                    <motion.div className={styles.cabeca} style={reduzido ? undefined : { y: cabecaY, opacity: cabecaOpacidade }}>
                        {sobretitulo ? (
                            <p className={styles.sobretitulo}>
                                <span className={styles.linhaHorizonte} aria-hidden="true" />
                                <span className={styles.sobretituloTexto}>{sobretitulo}</span>
                            </p>
                        ) : null}
                        <h1 className={styles.titulo}>
                            {palavras.map((p, i) => (
                                <React.Fragment key={`${p}-${i}`}>
                                    <span className={styles.mascara}>
                                        <span className={styles.palavra} style={{ animationDelay: `${0.12 + Math.min(i, 14) * 0.065}s` }}>
                                            {p}
                                        </span>
                                    </span>
                                    {i < palavras.length - 1 ? ' ' : null}
                                </React.Fragment>
                            ))}
                        </h1>
                        {subtitulo ? <p className={styles.subtitulo}>{subtitulo}</p> : null}
                    </motion.div>

                    {/* id="myLink": âncora do botão "BUSCA AVANÇADA" do topo (topo2). */}
                    <form id="myLink" className={styles.painel} onSubmit={onSubmit}>
                        {transacoes.length > 1 ? (
                            <div className={styles.abas} role="group" aria-label="Tipo de negócio">
                                {transacoes.map((t) => {
                                    const value = String(t.value).toLowerCase();
                                    const ativa = value === transacao;
                                    return (
                                        <button
                                            key={value}
                                            type="button"
                                            aria-pressed={ativa}
                                            className={`${styles.aba} ${ativa ? styles.abaAtiva : ''}`}
                                            style={ativa ? { color: corAbaAtivaTexto } : undefined}
                                            onClick={() => setTransacao(value)}
                                        >
                                            {ativa ? (
                                                <motion.span
                                                    layoutId={`hz-aba-${uid}`}
                                                    className={styles.abaIndicador}
                                                    transition={reduzido ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 34 }}
                                                    aria-hidden="true"
                                                />
                                            ) : null}
                                            <span className={styles.abaTexto}>{t.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : null}

                        <div className={styles.campos}>
                            {/* div, não label: o painel de sugestões mora dentro do campo. */}
                            <div className={styles.campoBloco}>
                                <span className={styles.rotulo}>Localização</span>
                                <CampoSelecaoMulti
                                    opcoes={allCities || []}
                                    valor={cidade}
                                    onChange={setCidade}
                                    rotuloVazio="Todas as cidades"
                                    plural="cidades"
                                    ariaLabel="Cidade"
                                    className={styles.campo}
                                />
                            </div>
                            <div className={styles.campoBloco}>
                                <span className={styles.rotulo}>Tipo de imóvel</span>
                                <CampoSelecaoMulti
                                    opcoes={allTypes || []}
                                    valor={tipo}
                                    onChange={setTipo}
                                    rotuloVazio="Todos os tipos"
                                    plural="tipos"
                                    ariaLabel="Tipo de imóvel"
                                    className={styles.campo}
                                />
                            </div>
                            <div className={`${styles.campoBloco} ${styles.campoCurto}`}>
                                <span className={styles.rotulo}>Quartos</span>
                                {/* vários números (2 e 3 quartos) — Daniel 20/10, igual à lateral de /imoveis */}
                                <CampoQuantidadeMulti
                                    valor={dormitorios}
                                    onChange={setDormitorios}
                                    rotuloVazio="Tanto faz"
                                    singular="quarto"
                                    plural="quartos"
                                    ariaLabel="Quartos"
                                    className={styles.campo}
                                />
                            </div>
                            <button type="submit" className={styles.buscar} disabled={loading} title="Buscar imóveis">
                                <span>{loading ? 'Buscando...' : 'Buscar'}</span>
                                <span className={styles.buscarSeta}>
                                    <IconeSeta />
                                </span>
                            </button>
                        </div>

                        <div className={styles.extras}>
                            <button
                                type="button"
                                className={styles.avancada}
                                aria-haspopup="dialog"
                                aria-expanded={avancadaAberta}
                                onClick={() => {
                                    trackMetaEvent(site, 'ViewContent', { content_name: 'Busca avançada', content_category: 'property_search' });
                                    setAvancadaAberta(true);
                                }}
                            >
                                Busca avançada
                                {qtdAvancada ? (
                                    <span className={styles.avancadaQtd} style={{ background: acao, color: corAcaoTexto }} aria-label={`${qtdAvancada} filtros ativos`}>
                                        {qtdAvancada}
                                    </span>
                                ) : null}
                                <IconeSeta />
                            </button>
                        </div>
                    </form>

                    <CardsBusca cards={allCards} site={site} variante="escuro" className={styles.cards} />
                </div>

                <BuscaAvancadaAurora
                    variante="horizonte"
                    aberto={avancadaAberta}
                    onFechar={fecharAvancada}
                    site={site}
                    origin={origin}
                    codsite={codsite2}
                    allCities={allCities || []}
                    allTypes={allTypes || []}
                    allCompany={allCompany}
                    basico={{ transacao, cidade, tipo, dormitorios }}
                    setDormitorios={setDormitorios}
                    finalize={finalize}
                    onBuscar={buscarAvancada}
                    onContagem={setQtdAvancada}
                    corAcao={acao}
                    corAcaoTexto={corAcaoTexto}
                    corTexto={corAbaAtivaTexto}
                />

                {/* ---------- role para baixo ---------- */}
                <button type="button" className={styles.rolar} onClick={rolarParaBaixo} aria-label="Rolar para ver os imóveis">
                    <span className={styles.rolarTexto}>Role</span>
                    <span className={styles.rolarTrilho} aria-hidden="true">
                        <motion.span
                            className={styles.rolarPonto}
                            initial={{ y: 0, opacity: 1 }}
                            animate={reduzido ? { y: 0, opacity: 1 } : { y: [0, 26, 26], opacity: [1, 1, 0] }}
                            transition={reduzido ? { duration: 0 } : { duration: 1.8, ease: EASE_HORIZONTE, repeat: Infinity, times: [0, 0.75, 1] }}
                        />
                    </span>
                </button>
            </section>
        </MotionConfig>
    );
};

class WelcomeHorizonte extends React.Component<HeroSectionProps, { hasError: boolean }> {
    constructor(props: HeroSectionProps) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro fallback sem quebrar a home.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <WelcomeHorizonteInner {...this.props} />;
    }
}

export default WelcomeHorizonte;
